import React, { useState, useEffect } from 'react';
import Navbar from '../common/Navbar';
import Calendar from '../common/Calendar';
import WorkoutLogModal from './WorkoutLogModal';
import api from '../../services/api';
import './AthleteWorkouts.css';

const AthleteWorkouts = () => {
  const [workouts, setWorkouts] = useState([]);
  const [programName, setProgramName] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState('threeWeeks');

  // Day viewer modal state
  const [showDayViewer, setShowDayViewer] = useState(false);
  const [viewingWorkout, setViewingWorkout] = useState(null);

  // Fetch workouts on mount and when window gains focus (to catch coach updates)
  useEffect(() => {
    fetchWorkouts();

    // Refetch when user returns to the page
    const handleFocus = () => {
      fetchWorkouts();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const fetchWorkouts = async () => {
    try {
      setLoading(true);

      // Fetch 3 months back through 12 months ahead so the calendar can page
      // across a full-length program, including one that hasn't started yet.
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 3);
      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + 12);

      const response = await api.get('/athlete/workouts', {
        params: {
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0]
        }
      });
      const workoutsData = response.data.workouts || [];
      setWorkouts(workoutsData);

      // Get program name from first workout if available
      if (workoutsData.length > 0 && workoutsData[0].programName) {
        setProgramName(workoutsData[0].programName);
      } else {
        setProgramName('');
      }
    } catch (err) {
      setError('Failed to load workouts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Format workouts for Calendar component (same format as coach)
  const getCalendarWorkouts = () => {
    return workouts
      .filter(w => w.exercises && w.exercises.length > 0)
      .map(w => ({
        date: w.date,
        exercises: w.exercises
      }));
  };

  const getWorkoutForDate = (date) => {
    return workouts.find(w => {
      const workoutDate = new Date(w.date);
      workoutDate.setHours(0, 0, 0, 0);
      const targetDate = new Date(date);
      targetDate.setHours(0, 0, 0, 0);
      return workoutDate.getTime() === targetDate.getTime();
    });
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    const workout = getWorkoutForDate(date);
    if (workout) {
      setViewingWorkout(workout);
    } else {
      // Create empty workout structure for display
      const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
      setViewingWorkout({
        date: date.toISOString(),
        dayOfWeek,
        title: '',
        exercises: []
      });
    }
    setShowDayViewer(true);
  };

  if (loading) {
    return (
      <div className="athlete-workouts-page">
        <Navbar />
        <div className="page-content">
          <div className="loading">Loading workouts...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="athlete-workouts-page">
      <Navbar />
      <div className="page-content">
        <div className="page-header">
          <div>
            <h1>Workout Schedule</h1>
            <p className="subtitle">View your assigned workouts and upcoming training</p>
          </div>
        </div>

        {/* Program Badge */}
        {programName ? (
          <div className="program-badge">
            <span className="program-badge-icon">📋</span>
            <span className="program-badge-name">{programName}</span>
          </div>
        ) : (
          <div className="program-badge no-program">
            <span className="program-badge-icon">⚠️</span>
            <span className="program-badge-name">No Program Assigned</span>
          </div>
        )}

        {error && <div className="error-banner">{error}</div>}

        {/* View Selector Toolbar */}
        <div className="workouts-toolbar">
          <div className="view-toggle">
            <button
              className={`toggle-btn ${view === 'daily' ? 'active' : ''}`}
              onClick={() => setView('daily')}
            >
              Day
            </button>
            <button
              className={`toggle-btn ${view === 'weekly' ? 'active' : ''}`}
              onClick={() => setView('weekly')}
            >
              Week
            </button>
            <button
              className={`toggle-btn ${view === 'threeWeeks' ? 'active' : ''}`}
              onClick={() => setView('threeWeeks')}
            >
              3 Weeks
            </button>
            <button
              className={`toggle-btn ${view === 'monthly' ? 'active' : ''}`}
              onClick={() => setView('monthly')}
            >
              Month
            </button>
          </div>
        </div>

        <div className="calendar-container">
          <Calendar
            selectedDate={selectedDate}
            onDateSelect={handleDateSelect}
            workouts={getCalendarWorkouts()}
            view={view}
            onViewChange={setView}
          />
        </div>
      </div>

      {/* Workout Log Modal - Athletes can input their weights */}
      <WorkoutLogModal
        isOpen={showDayViewer}
        onClose={() => {
          setShowDayViewer(false);
          setViewingWorkout(null);
        }}
        workout={viewingWorkout}
        date={selectedDate}
      />
    </div>
  );
};

export default AthleteWorkouts;
